/**
 * XP celebration constants: testIDs, palette and the animation timeline.
 * Timeline values mirror the design file (`Rank Up Celebrations.dc.html`) —
 * see specs/xp-rank-celebrations/plan.md before changing any number.
 */

export const CELEBRATION_TEST_IDS = {
  LEVEL_UP: 'level-up-celebration',
  LEVEL_UP_NUMBER: 'level-up-number',
  LEVEL_UP_LEVEL: 'level-up-level',
  LEVEL_UP_XP: 'level-up-xp',
  LEVEL_UP_NEXT: 'level-up-next',
  LEVEL_UP_BAR_FILL: 'level-up-bar-fill',
  LEVEL_UP_CONTINUE: 'level-up-continue',
  RANK_UP: 'rank-up-celebration',
  RANK_UP_TRANSITION: 'rank-up-transition',
  RANK_UP_NAME: 'rank-up-name',
  RANK_UP_LEVEL_CHIP: 'rank-up-level-chip',
  RANK_UP_XP: 'rank-up-xp',
  RANK_UP_NEXT: 'rank-up-next',
  RANK_UP_CONTINUE: 'rank-up-continue',
  RANK_UP_SHARE: 'rank-up-share',
} as const;

export const XP_HEADER_TEST_IDS = {
  CLUSTER: 'xp-header-cluster',
  LEVEL_PILL: 'xp-header-level-pill',
  RANK_BLOCK: 'xp-header-rank-block',
  RANK_NAME: 'xp-header-rank-name',
} as const;

export const XP_GAIN_FLASH_TEST_ID = 'xp-gain-flash';

/** "+X XP" flash timeline (ms). Floats up + fades, then self-dismisses. */
export const XP_GAIN_FLASH = {
  RISE_MS: 1100,
  HOLD_MS: 350,
  FADE_MS: 450,
  TRAVEL: 26,
} as const;

// Design palette (design-system constants, not theme-derived — DailyRewardModal precedent)
export const CELEBRATION_COLORS = {
  PURPLE: '#A78BFA',
  MINT: '#6EE7B7',
  GOLD: '#FCD34D',
  WHITE: '#FFFFFF',
  SURFACE: '#1F2937',
  TRACK: '#374151',
  INK: '#111827',
  TEXT: '#F9FAFB',
  MUTED: '#9CA3AF',
  HINT: '#6B7280',
  SCRIM: 'rgba(10, 6, 20, 0.92)',
} as const;

export const BURST_COLORS = [
  CELEBRATION_COLORS.PURPLE,
  CELEBRATION_COLORS.MINT,
  CELEBRATION_COLORS.GOLD,
  CELEBRATION_COLORS.WHITE,
];

export const LEVEL_UP_SPARK_COUNT = 10;
export const LEVEL_STAGE_SIZE = 220;
export const LEVEL_NUMBER_SIZE = 88;
export const FORGE_BURST_COUNT = 22;

/**
 * Level-up "Number Slam" timeline (ms from mount), mirroring the design file's
 * `showLevel` variant. The number slams in over rays/shockwave/sparks, then text,
 * XP bar and Continue rise on staggered delays. The blocking modal has no
 * auto-dismiss — Continue is the only exit. `filter: blur` from the design is
 * approximated with scale+opacity (not native-supported); the number glow is a
 * static gold textShadow, not the animated CSS `zpNumGlow`.
 */
export const LEVEL_SLAM = {
  SCRIM_IN_MS: 280,
  NUM_SLAM_MS: 750,
  RING_POP_DELAY_MS: 250,
  RING_POP_MS: 600,
  SHOCK_DELAY_MS: 320,
  SHOCK_MS: 700,
  RAYS_DELAY_MS: 300,
  RAYS_IN_MS: 700,
  RAYS_SPIN_MS: 12000,
  SPARK_DELAY_MS: 120,
  SPARK_MS: 700,
  IMPACT_MS: 380,
  RISE_EYEBROW_MS: 600,
  RISE_HEADING_MS: 720,
  RISE_CHIP_MS: 840,
  RISE_XP_MS: 960,
  RISE_BUTTON_MS: 1040,
  BAR_FILL_DELAY_MS: 1150,
} as const;

/**
 * Level-up XP bar choreography (ms): the bar fills from its starting point to 100%
 * (completing the old level), holds, snaps back to 0% at the boundary, then fills to
 * the overflow remainder carried into the new level. `DEFAULT_START` is where the bar
 * begins when the pre-earn fill is unknown — near-full so the "completing the level"
 * beat always reads.
 * See specs/xp-live-feedback/plan.md before changing these numbers.
 */
export const LEVEL_UP_BAR = {
  START_DELAY_MS: 400,
  FILL_MS: 700,
  HOLD_FULL_MS: 280,
  RESET_MS: 220,
  REFILL_MS: 800,
  DEFAULT_START: 0.82,
} as const;

/** Badge Forge timeline (ms from ceremony mount). Sound impact is baked at 950ms. */
export const FORGE_TIMELINE = {
  SCRIM_IN_MS: 280,
  SHAKE_MS: 900,
  FLASH_DELAY_MS: 880,
  FRAGMENTS_DELAY_MS: 920,
  FRAGMENTS_MS: 800,
  SLAM_DELAY_MS: 1000,
  SLAM_MS: 700,
  RAYS_DELAY_MS: 1000,
  RAYS_IN_MS: 700,
  RAYS_SPIN_MS: 14000,
  BURST_DELAY_MS: 950,
  IMPACT_MS: 950,
  RISE_TRANSITION_MS: 1550,
  RISE_NAME_MS: 1680,
  RISE_CHIP_MS: 1800,
  RISE_XP_MS: 1920,
  XP_FILL_DELAY_MS: 2050,
  XP_FILL_MS: 1000,
  RISE_BUTTONS_MS: 2100,
} as const;

/** Old-badge fragment flings (design: polygon shards; here quadrant crops). */
export const FORGE_FRAGMENT_FLINGS = [
  { fx: -95, fy: -75, fr: '-38deg' },
  { fx: 100, fy: -62, fr: '42deg' },
  { fx: -85, fy: 90, fr: '33deg' },
  { fx: 90, fy: 95, fr: '-32deg' },
] as const;

export const FORGE_BADGE_SIZE = 168;
export const FORGE_STAGE_SIZE = 240;
